/**
 * ClientOverlapReport.tsx — Client-facing overlap analysis report.
 * Route: /reports/client-overlap
 *
 * Section A: Portfolio Summary (stat cards)
 * Section B: Per-Litigation Breakdown (repeating cards)
 * Section C: Export Actions
 */
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  FileSpreadsheet, Download, FileText, CheckCircle,
  AlertTriangle, Clock, TrendingUp, Users, DollarSign, Info
} from 'lucide-react';
import { useClientOverlapReport, STATUS_DISPLAY } from '@/hooks/reports/useClientOverlapReport';
import type { OverlapStatus, ConfidenceScore } from '@/types/cmm/claimant.types';

// ── Helpers ──────────────────────────────────────────────────
const formatCurrency = (v: number): string =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const CONFIDENCE_BADGE: Record<ConfidenceScore, { cls: string; label: string }> = {
  confirmed:  { cls: 'bg-green-100 text-green-800 border-green-200', label: 'Confirmed' },
  probable:   { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Probable' },
  unverified: { cls: 'bg-gray-100 text-gray-800 border-gray-200',   label: 'Unverified' },
};

const STATUS_BADGE_STYLE: Record<OverlapStatus, { cls: string; label: string }> = {
  CONFIRMED:      { cls: 'bg-green-100 text-green-800 border-green-200', label: 'Accepted' },
  PENDING_REVIEW: { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending' },
  REJECTED:       { cls: 'bg-red-100 text-red-800 border-red-200',       label: 'Rejected' },
};

// ── Stat Card sub-component ─────────────────────────────────
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, description }) => (
  <Card className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-200 rounded-xl">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <span className="text-3xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <div className="p-3 bg-blue-100 rounded-lg">
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ── Component ────────────────────────────────────────────────
const ClientOverlapReport: React.FC = () => {
  const {
    portfolioSummary,
    litigationBreakdowns,
    exportExcel,
    exportCSV,
  } = useClientOverlapReport();

  return (
    <ContentWrapper>
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="h-7 w-7 text-blue-600" aria-label="Report icon" />
              Client Overlap Report
            </h1>
            <p className="text-gray-500 mt-1">
              Portfolio overlap analysis — generated {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md px-3 py-1 text-sm font-semibold rounded-full">
            BETA
          </Badge>
        </div>
      </div>

      {/* ══════ SECTION A — Portfolio Summary ══════ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total Unmonetized Value"
          value={formatCurrency(portfolioSummary.totalUnmonetizedValue)}
          icon={<DollarSign className="h-6 w-6 text-blue-600" aria-label="Dollar sign" />}
          description="Aggregate est. value across active overlaps"
        />
        <StatCard
          title="Claimants Analyzed"
          value={portfolioSummary.totalClaimantsAnalyzed}
          icon={<Users className="h-6 w-6 text-blue-600" aria-label="Users" />}
          description="Total claimants in portfolio"
        />
        <StatCard
          title="Overlaps Identified"
          value={portfolioSummary.totalWithOverlaps}
          icon={<TrendingUp className="h-6 w-6 text-blue-600" aria-label="Trending up" />}
          description="Unique claimants with at least 1 active overlap"
        />
      </div>

      {/* ══════ SECTION B — Per-Litigation Breakdown ══════ */}
      <div className="space-y-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900">Litigation Breakdown</h2>

        {litigationBreakdowns.map((lit) => (
          <Card key={lit.litigationName} className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-200 rounded-xl">
            <CardHeader className="border-b border-gray-100 pb-4 mb-6">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" aria-label="Spreadsheet" />
                {lit.litigationName}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* B.1 — Narrative Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed">{lit.narrativeSummary}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lit.rulesApplied.map(r => (
                    <Badge key={r} className="bg-blue-100 text-blue-800 border border-blue-200 px-2 py-1 text-xs font-semibold rounded-full">{r}</Badge>
                  ))}
                </div>
              </div>

              {/* B.2 — Counts */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" aria-label="Total" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Total Overlaps</p>
                    <p className="text-xl font-bold text-gray-900">{lit.totalOverlaps}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" aria-label="Completed" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Completed</p>
                    <p className="text-xl font-bold text-gray-900">{lit.completedOverlaps}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" aria-label="With deficiencies" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">With Deficiencies</p>
                    <p className="text-xl font-bold text-gray-900">{lit.deficientOverlaps}</p>
                  </div>
                </div>
              </div>

              {/* B.3 — Claims Detail Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left">
                      <th className="py-2 pr-4 font-medium text-gray-600">ClaimsIQ ID</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">Claimant</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">State</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">Match Type</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">Confidence</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">Est. Value</th>
                      <th className="py-2 pr-4 font-medium text-gray-600">Status</th>
                      <th className="py-2 font-medium text-gray-600">Open Deficiencies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lit.rows.map((row) => {
                      const openDefs = row.deficiencies.filter(d => !d.clearedAt).length;
                      return (
                        <tr key={row.claimantId + '-' + row.ruleApplied} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 pr-4 font-mono text-xs text-gray-700">{row.claimantId}</td>
                          <td className="py-2 pr-4 text-gray-900">{row.claimantName}</td>
                          <td className="py-2 pr-4 text-gray-700">{row.state}</td>
                          <td className="py-2 pr-4">
                            <Badge className="bg-gray-100 text-gray-800 border border-gray-200 px-2 py-1 text-xs font-semibold rounded-full">{row.matchType}</Badge>
                          </td>
                          <td className="py-2 pr-4">
                            <Badge className={CONFIDENCE_BADGE[row.confidenceScore].cls + ' border px-2 py-1 text-xs font-semibold rounded-full'}>
                              {CONFIDENCE_BADGE[row.confidenceScore].label}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4 font-medium text-gray-900">{formatCurrency(row.estClaimValue)}</td>
                          <td className="py-2 pr-4">
                            <Badge className={STATUS_BADGE_STYLE[row.overlapStatus].cls + ' border px-2 py-1 text-xs font-semibold rounded-full'}>
                              {STATUS_BADGE_STYLE[row.overlapStatus].label}
                            </Badge>
                          </td>
                          <td className="py-2 text-center">
                            {openDefs > 0
                              ? <span className="inline-flex items-center gap-1 text-amber-700 font-medium"><AlertTriangle className="h-3.5 w-3.5" aria-label="Warning" />{openDefs}</span>
                              : <span className="text-green-600 font-medium">&mdash;</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ══════ SECTION C — Export Actions ══════ */}
      <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 shadow-sm rounded-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Export Report</h3>
              <p className="text-sm text-gray-600 mt-1">
                Download the full report as a multi-sheet Excel workbook or a flat CSV file.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl font-semibold rounded-lg px-4 py-2"
                onClick={exportExcel}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Full Report (Excel)
              </Button>
              <Button
                variant="outline"
                className="font-semibold rounded-lg px-4 py-2"
                onClick={exportCSV}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer note */}
      <Alert className="mt-6 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          This report is generated from ClaimsIQ's Archetype Overlap Engine analysis.
          All values are estimates and subject to final verification.
          Deficiency items require client action before claims can be finalized.
        </AlertDescription>
      </Alert>
    </ContentWrapper>
  );
};

export default ClientOverlapReport;
